import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  MemberNameCell,
  StackedCellText,
  StackedListCell,
  WrappingCellText,
} from '@/components/common/DataTable';
import { HighlightText } from '@/components/common/HighlightText';
import { MeetPeopleCell } from '@/components/common/MeetPeopleCell';
import { RowActionButton, RowViewLink } from '@/components/common/RowActions';
import { WorkTypeBadge } from '@/components/common/WorkTypeBadge';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { STATUS_TEXT_CLASS } from '@/styles/statusColors';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { formatLocation } from '@/utils/formatLocation';
import { formatNumber } from '@/utils/number';
import type {
  AssignedInstitute,
  DcrSheetEntry,
  DcrSheetExpense,
  DistributionInstitute,
  DistributionMember,
  SpecimenDistributionRow,
  SpecimenDrillSearch,
  SpecimenInstitutionRow,
} from '../types/product.types';
import { formatDistributedPercent, getInHandQty } from '../utils/distribution';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const qty = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

const inHandCell = (receivedQty: number, distributedQty: number) => {
  const balance = getInHandQty(receivedQty, distributedQty);
  return (
    <span className={balance < 0 ? 'tabular-nums text-destructive' : 'tabular-nums'}>
      {formatNumber(balance)}
    </span>
  );
};

const timeRange = (timeFrom: string | null, timeTo: string | null): string | undefined =>
  timeFrom === null && timeTo === null
    ? undefined
    : `${formatTime(timeFrom)} – ${formatTime(timeTo)}`;

const getViewZmsColumn = (zmSearch: SpecimenDrillSearch) =>
  createActionsColumn<SpecimenDistributionRow>((specimen) => (
    <RowViewLink
      to="/product/specimen-distribution/$specimenId"
      params={{ specimenId: specimen.specimenId }}
      search={zmSearch}
      label="View ZMs"
      aria-label={`ZMs who handed out ${specimen.name}`}
    />
  ));

export function getSpecimenDistributionColumns(
  zmSearch: SpecimenDrillSearch,
  searchText?: string,
): ColumnDef<SpecimenDistributionRow, unknown>[] {
  return [
    createSerialColumn<SpecimenDistributionRow>(),
    {
      accessorKey: 'name',
      header: 'Specimen Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={row.original.nameLocal}
        />
      ),
    },
    {
      accessorKey: 'spCode',
      header: 'SP Code / Bar Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.spCode}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.barCode}
        />
      ),
    },
    {
      accessorKey: 'editionName',
      header: 'Edition',
      cell: ({ row }) => dash(row.original.editionName),
    },
    {
      accessorKey: 'distributedQty',
      header: 'Distributed Qty',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    getViewZmsColumn(zmSearch),
  ];
}

export function getDistributionMemberColumns(
  specimenId: number,
  zmSearch: SpecimenDrillSearch,
  searchText: string | undefined,
): ColumnDef<DistributionMember, unknown>[] {
  return [
    createSerialColumn<DistributionMember>(),
    {
      accessorKey: 'name',
      header: 'ZM Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.name,
            hrEmpId: row.original.hrEmpId,
            mobile: row.original.userMobile,
          }}
          searchText={searchText}
        />
      ),
    },
    {
      accessorKey: 'designationName',
      header: 'Designation',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.designationName)}</WrappingCellText>,
    },
    {
      accessorKey: 'thanaName',
      header: 'Area',
      cell: ({ row }) => (
        <WrappingCellText>
          {dash(row.original.thanaName ?? row.original.districtName)}
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'challanReceived',
      header: 'Received',
      cell: ({ row }) => qty(row.original.challanReceived),
    },
    {
      accessorKey: 'distributedQty',
      header: 'Distributed',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    createActionsColumn<DistributionMember>((member) => (
      <RowViewLink
        to="/product/specimen-distribution/$specimenId/$zmId"
        params={{ specimenId, zmId: member.userId }}
        search={zmSearch}
        label="View Institutes"
        aria-label={`Institutes where ${member.name} handed it out`}
      />
    )),
  ];
}

export function getSpecimenAdjustmentColumns(
  zmSearch: SpecimenDrillSearch,
  searchText?: string,
): ColumnDef<SpecimenDistributionRow, unknown>[] {
  return [
    createSerialColumn<SpecimenDistributionRow>(),
    {
      accessorKey: 'name',
      header: 'Specimen Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={row.original.nameLocal}
        />
      ),
    },
    {
      accessorKey: 'spCode',
      header: 'SP Code / Bar Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.spCode}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.barCode}
        />
      ),
    },
    {
      accessorKey: 'challanQty',
      header: 'Challan Qty',
      cell: ({ row }) => qty(row.original.challanQty),
    },
    {
      accessorKey: 'distributedQty',
      header: 'Distributed Qty',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    {
      id: 'inHand',
      header: 'Hand Qty',
      cell: ({ row }) => inHandCell(row.original.challanQty, row.original.distributedQty),
    },
    {
      id: 'distributedPct',
      header: 'Distributed (%)',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatDistributedPercent(row.original.distributedQty, row.original.challanQty)}
        </span>
      ),
    },
    getViewZmsColumn(zmSearch),
  ];
}

export function getDistributionInstituteColumns(): ColumnDef<DistributionInstitute, unknown>[] {
  return [
    createSerialColumn<DistributionInstitute>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.instituteName)}</WrappingCellText>,
    },
    {
      accessorKey: 'instituteThana',
      header: 'Thana',
      cell: ({ row }) => dash(row.original.instituteThana),
    },
    {
      accessorKey: 'givenDate',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.givenDate),
    },
    {
      accessorKey: 'workTypeCode',
      header: 'Work',
      cell: ({ row }) => dash(row.original.workTypeCode),
    },
    {
      accessorKey: 'teacherMobiles',
      header: 'Received By',
      cell: ({ row }) => <StackedListCell listText={row.original.teacherMobiles} />,
    },
    {
      accessorKey: 'distributedQty',
      header: 'Qty',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
  ];
}

const highlightOrDash = (text: string | number | null, searchText: string | undefined) =>
  text === null || text === '' ? (
    EMPTY_VALUE
  ) : (
    <HighlightText text={String(text)} highlightedText={searchText} />
  );

interface SpecimenInstitutionColumnOptions {
  onEditTeacherName: (entry: SpecimenInstitutionRow) => void;
  searchText: string | undefined;
}

export function getSpecimenInstitutionColumns({
  onEditTeacherName,
  searchText,
}: SpecimenInstitutionColumnOptions): ColumnDef<SpecimenInstitutionRow, unknown>[] {
  return [
    createSerialColumn<SpecimenInstitutionRow>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute / Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightOrDash(row.original.instituteName, searchText)}
          primaryClassName="font-medium"
          secondary={
            row.original.instituteCode === null
              ? null
              : highlightOrDash(row.original.instituteCode, searchText)
          }
        />
      ),
    },
    {
      accessorKey: 'instituteThana',
      header: 'Thana / District',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.instituteThana)}
          secondary={row.original.instituteDistrict}
        />
      ),
    },
    {
      accessorKey: 'teacherName',
      header: 'Teacher / Mobile',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightOrDash(row.original.teacherName, searchText)}
          secondary={
            row.original.teacherMobile === null
              ? null
              : highlightOrDash(row.original.teacherMobile, searchText)
          }
          tertiary={
            row.original.isTeacherNameEdited ? (
              <span className={STATUS_TEXT_CLASS.warning}>Name edited</span>
            ) : null
          }
        />
      ),
    },
    {
      accessorKey: 'specimenName',
      header: 'Specimen / Edition',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightOrDash(row.original.specimenName, searchText)}
          secondary={row.original.editionName}
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'ZM Name / Date',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightOrDash(row.original.name, searchText)}
          secondary={formatDisplayDate(row.original.dcrDate)}
        />
      ),
    },
    {
      accessorKey: 'specimenQty',
      header: 'Qty',
      cell: ({ row }) => qty(row.original.specimenQty),
    },
    createActionsColumn<SpecimenInstitutionRow>((entry) => (
      <RowActionButton
        label="Edit Name"
        ariaLabel={`Edit the teacher name on ${entry.instituteName}`}
        onClick={() => onEditTeacherName(entry)}
      />
    )),
  ];
}

export function getSheetEntryColumns(
  searchText: string | undefined,
): ColumnDef<DcrSheetEntry, unknown>[] {
  return [
    createSerialColumn<DcrSheetEntry>(),
    {
      accessorKey: 'dcrDate',
      header: 'Date / Time',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.dcrDate)}
          primaryClassName="font-medium"
          secondary={timeRange(row.original.timeFrom, row.original.timeTo)}
        />
      ),
    },
    {
      accessorKey: 'workTypeCode',
      header: 'Work Type',
      cell: ({ row }) => (
        <WorkTypeBadge code={row.original.workTypeCode} name={row.original.workType} />
      ),
    },
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={highlightOrDash(row.original.instituteName, searchText)}
          secondary={formatLocation({
            thanaName: row.original.instituteThana,
            districtName: row.original.instituteDistrict,
            divisionName: row.original.instituteDivision,
          })}
        />
      ),
    },
    {
      id: 'area',
      header: 'Area From / To',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.areaFrom)}
          secondary={dash(row.original.areaTo)}
        />
      ),
    },
    {
      accessorKey: 'transportType',
      header: 'Transport',
      cell: ({ row }) => dash(row.original.transportType),
    },
    {
      id: 'meetPeople',
      header: 'Meet Person',
      cell: ({ row }) => <MeetPeopleCell meetPeople={row.original.meetPeople} />,
    },
    {
      accessorKey: 'specimenQty',
      header: 'Specimen',
      cell: ({ row }) => qty(row.original.specimenQty),
    },
    {
      accessorKey: 'fareAmount',
      header: 'Fare',
      cell: ({ row }) => qty(row.original.fareAmount),
    },
    {
      accessorKey: 'approveFareAmount',
      header: 'Approved',
      cell: ({ row }) => qty(row.original.approveFareAmount),
    },
  ];
}

export function getSheetExpenseColumns(
  searchText: string | undefined,
): ColumnDef<DcrSheetExpense, unknown>[] {
  return [
    createSerialColumn<DcrSheetExpense>(),
    {
      accessorKey: 'expenseDate',
      header: 'Date',
      cell: ({ row }) => (
        <span className="font-medium">{formatDisplayDate(row.original.expenseDate)}</span>
      ),
    },
    {
      accessorKey: 'expenseType',
      header: 'Expense',
      cell: ({ row }) => highlightOrDash(row.original.expenseType, searchText),
    },
    {
      accessorKey: 'vendor',
      header: 'Vendor',
      cell: ({ row }) => highlightOrDash(row.original.vendor, searchText),
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.comments)}</WrappingCellText>,
    },
    { accessorKey: 'amount', header: 'Claimed', cell: ({ row }) => qty(row.original.amount) },
    {
      accessorKey: 'approveAmount',
      header: 'Approved',
      cell: ({ row }) => qty(row.original.approveAmount),
    },
  ];
}

export function getAssignedInstituteColumns(
  visited: boolean,
  searchText: string | undefined,
): ColumnDef<AssignedInstitute, unknown>[] {
  const columns: ColumnDef<AssignedInstitute, unknown>[] = [
    createSerialColumn<AssignedInstitute>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.instituteName} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'eiinNumber',
      header: 'EIIN',
      cell: ({ row }) => (
        <span className="text-xs tabular-nums">
          {highlightOrDash(row.original.eiinNumber, searchText)}
        </span>
      ),
    },
    { accessorKey: 'thanaName', header: 'Thana', cell: ({ row }) => dash(row.original.thanaName) },
    {
      accessorKey: 'districtName',
      header: 'District',
      cell: ({ row }) => dash(row.original.districtName),
    },
  ];

  if (visited) {
    columns.push(
      {
        accessorKey: 'visitDays',
        header: 'Days Visited',
        cell: ({ row }) => qty(row.original.visitDays),
      },
      {
        accessorKey: 'lastVisit',
        header: 'Last Visit',
        cell: ({ row }) => formatDisplayDate(row.original.lastVisit),
      },
    );
  }

  return columns;
}
